/* cenario.ts — a fotografia da operação a um dado preço.
 *
 * Premissas de negócio que só existem aqui (vêm do modelo de venda solar):
 *
 *   1. O KIT é faturado pela DISTRIBUIDORA; o integrador fatura o SERVIÇO.
 *      Base de serviço = preço − kit. Comissão e tributo incidem sobre o total
 *      ou só sobre o serviço, conforme a configuração de CADA UM.
 *
 *   2. Despesa fixa é MENSAL, e o rateio por projeto é SEMPRE POR VOLUME:
 *      (despFixa × 12) ÷ V12. Ratear como percentual do ticket foi ABOLIDO —
 *      fazia o custo fixo crescer junto com o preço sugerido, então cada
 *      aumento de preço aumentava o custo, e a meta de lucro nunca fechava:
 *      o preço sugerido divergia para o infinito.
 *
 *   3. Unidade, mês e ano saem da MESMA conta. O mensal é o anual ÷ 12 por
 *      construção — não uma segunda conta que pode divergir. No app antigo
 *      havia dois motores (um mensal na home, um anual no relatório) e eles
 *      mostravam números diferentes para a mesma empresa.
 *
 *   4. Grandeza indefinida é `null`, NUNCA NaN ou Infinity. Sem volume não
 *      existe rateio de fixa, logo não existe lucro — e a tela escreve "—".
 *      NaN vazaria para o formatador e apareceria como "R$ NaN" na cara do
 *      usuário; pior, sobreviveria a comparações sem disparar erro nenhum.
 */
/**
 * percentuaisVariaveis(p): soma, em FRAÇÃO, de tudo que é percentual sobre o
 * preço — comissão, tributo, "outras" e TODAS as despesas extras — separado
 * pela base sobre a qual incide.
 *
 * Que as extras entrem aqui é o conserto de um bug real: elas ficavam fora do
 * denominador do preço sugerido, então o preço "que entrega 12%" entregava 9%.
 * Quem soma percentual tem de somar todos, sempre no mesmo lugar.
 */
export function percentuaisVariaveis(p) {
    let total = p.outras / 100;
    let serv = 0;
    if (p.comissaoBase === 'servico')
        serv += p.comissao / 100;
    else
        total += p.comissao / 100;
    if (p.tributoBase === 'servico')
        serv += p.tributo / 100;
    else
        total += p.tributo / 100;
    for (const e of p.despesasExtras) {
        if (e.base === 'servico')
            serv += e.valor / 100;
        else
            total += e.valor / 100;
    }
    return { total, serv };
}
/**
 * cenario(preco, p): tudo que se pode dizer da operação vendendo a `preco`.
 *
 * É a função mais chamada do sistema: a home recalcula a cada tecla digitada,
 * o relatório chama uma vez por linha e `recomendar` chama duas vezes. Por isso
 * ela não faz I/O, não memoiza e não guarda estado — dado entra, número sai.
 */
/**
 * Rateio da despesa fixa POR VOLUME (premissa 3): (fixa × 12) ÷ projetos/ano.
 *
 * Sem volume não há como ratear: devolve null e o lucro deixa de existir —
 * não vira "lucro = MC", que seria otimismo inventado. É a ÚNICA conta do
 * rateio; painel, laudo e inversões de preço leem daqui para não divergirem.
 */
export function rateioFixaUnit(p) {
    return p.V12 > 0 ? (p.despFixa * 12) / p.V12 : null;
}
export function cenario(preco, p) {
    const tk = preco;
    // A base de serviço pode ficar NEGATIVA se o preço for menor que o kit, e o
    // cenário TEM de mostrar isso: é um projeto vendido abaixo do custo do kit, e
    // esconder num null seria omitir o diagnóstico. `baseServico` sai negativa.
    const srv = tk - p.kit;
    // Mas a base de INCIDÊNCIA não acompanha o sinal, e essa é a distinção que
    // faltava aqui. Percentual sobre base negativa produzia tributo e comissão
    // NEGATIVOS, que entravam somando margem: com kit 20.000 e preço 15.000 o
    // motor mostrava prejuízo de R$ 8.450 onde o real era R$ 9.000, subdeclarando
    // R$ 550 sempre para o lado bom. Nenhum fisco devolve tributo por faturamento
    // negativo e nenhum vendedor devolve comissão.
    //
    // A inversão de `precoSugerido` conhece este clamp: quando a raiz cai
    // abaixo do kit (possível com custo direto rebaixado por override), ela
    // resolve o trecho sem base de serviço em vez de assumir a equação linear.
    const srvIncid = Math.max(0, srv);
    const cBase = p.comissaoBase === 'servico' ? srvIncid : tk;
    const tBase = p.tributoBase === 'servico' ? srvIncid : tk;
    const comissaoR = (p.comissao / 100) * cBase;
    const tributoR = (p.tributo / 100) * tBase;
    const outrasR = (p.outras / 100) * tk;
    const extrasDetalhes = p.despesasExtras.map((e) => ({
        nome: e.nome,
        v: (e.valor / 100) * (e.base === 'servico' ? srvIncid : tk),
    }));
    const extrasR = extrasDetalhes.reduce((s, e) => s + e.v, 0);
    /** Despesa variável por projeto, em R$. */
    const dvUnit = comissaoR + tributoR + outrasR + extrasR;
    /** Margem de contribuição: o que sobra do preço depois do custo direto e das variáveis. */
    const mcUnit = tk - p.cdt - dvUnit;
    const mcPct = tk > 0 ? mcUnit / tk : null;
    const fixUnit = rateioFixaUnit(p);
    const lucroUnit = fixUnit === null ? null : mcUnit - fixUnit;
    const lucroPct = lucroUnit === null || tk <= 0 ? null : lucroUnit / tk;
    const qtdMes = p.V12 / 12;
    const anual = {
        faturamento: tk * p.V12,
        mc: mcUnit * p.V12,
        despFixa: p.despFixa * 12,
        lucro: lucroUnit === null ? null : lucroUnit * p.V12,
    };
    // O mensal é DERIVADO do anual. Escrever `despFixa: p.despFixa` aqui é o
    // mesmo que `anual.despFixa / 12` — mantido explícito porque é o dado que o
    // usuário digitou, e ver o próprio número na tela mensal gera confiança.
    const mensal = {
        faturamento: anual.faturamento / 12,
        mc: anual.mc / 12,
        despFixa: p.despFixa,
        lucro: anual.lucro === null ? null : anual.lucro / 12,
        projetos: qtdMes,
    };
    // Ponto de equilíbrio (média mensal): quantos projetos / quanto faturamento
    // cobrem a despesa fixa do mês. Só existe com MC positiva — com MC negativa
    // nenhum volume paga a conta, e devolver um número aqui sugeriria que sim.
    const peQtd = mcUnit > 0 ? p.despFixa / mcUnit : null;
    const peFat = mcPct !== null && mcPct > 0 ? p.despFixa / mcPct : null;
    return {
        ticket: tk,
        baseServico: srv,
        comissaoR,
        tributoR,
        outrasR,
        extrasR,
        extrasDetalhes,
        dvUnit,
        custoVariavelPct: tk > 0 ? dvUnit / tk : null,
        mcUnit,
        mcPct,
        fixUnit,
        lucroUnit,
        lucroPct,
        mensal,
        anual,
        qtdMes,
        peQtd,
        peFat,
    };
}
//# sourceMappingURL=cenario.js.map